// ExtraCreditCourseDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        tabs: { set: "Set Rules", apply: "Apply Rules" },
        loading: {
            assignments: "Loading assignments…",
            rules: "Loading rules…",
            students: "Loading students…",
            generic: "Loading…",
        },
        errors: {
            missingParams: "Missing required params.",
            auth401: "Authentication failed (401).",
            loadCourse: "Failed to load course data.",
            loadRules: "Failed to load rules.",
            loadColumns: "Failed to load gradebook columns.",
            loadAttempts: "Failed to load attempts.",
            loadStudents: "Failed to load students.",
            enterNonNegative: "Enter a non-negative number.",
        },
        headings: {
            assoc: "Assignment Extra Credit Associations",
            apply: "Apply Rules — Student × Assignment",
            student: "Your Extra Credit",
            total: "Total Extra Credit:",
        },
        tooltips: {
            total: "Sum of applicable extra credit across assignments",
            updateExtraCredit:
                "Create/Find 'Extra Credit' column and write totals; also stores extra-credit snapshot",
            sortByAssignment: "Sort assignment columns by title",
            sortByStudent: "Sort by student name",
            sortBy: "Sort by {label}",
            clearRuleFor: "Clear rule for {title}",
            selectRuleFor: "Select rule for {title}",
        },
        buttons: {
            saveAll: "Save all",
            deleteAll: "Delete all",
            retry: "Retry",
            applyAndSave: "Apply & save",
            clearRule: "Clear rule",
            applyToAll: "Apply to all:",
            clearOption: "(clear)",
            sortAssignmentsAZ: "Sort Assignments A→Z",
            sortAssignmentsZA: "Sort Assignments Z→A",
            sortStudentsAZ: "Sort Students A→Z",
            sortStudentsZA: "Sort Students Z→A",
            updateExtraCredit: "Update Extra Credit",
            updating: "Updating…",
            save: "Save",
            saving: "Saving…",
            clear: "Clear",
        },
        table: {
            actions: "Actions",
            assignment: "Assignment",
            rule: "Rule",
            due: "Due",
            points: "Points",
            attempted: "Attempted",
            credit: "Extra Credit",
            student: "Student",
            noAssignments: "No assignments found.",
            noStudents: "No students found.",
            columnsUnavailable: "columns unavailable",
            rulesUnavailable: "Rules unavailable",
            unknown: "Unknown",
        },
        meta: { points: "Points:", due: "Due:" },
        chips: { overridden: "Overridden" },
        attemptCard: {
            attempt: "Attempt:",
            status: "Status:",
            credit: "Credit:",
            noAttempt: "No attempt yet",
            overrideLabel: "Override:",
        },
        notices: {
            deletedOne: "Deleted rule (and any overrides/credits on that item)",
            savedRule: "Saved rule",
            appliedAll: "Applied to all & saved",
            savedAll: "Saved all rules",
            deletedAll: "Deleted all rules (overrides & credits) for this course",
            noStudentsOrCourse: "No students or course selected.",
            updatedExtraCredit:
                "Updated extra credit for {count} {studentWord} and stored snapshots.",
            syncFailed: "Sync failed:",
            bulkSaveFailed: "Bulk save failed:",
            saveAllFailed: "Save all failed:",
            deleteAllFailed: "Delete all failed:",
            saveFailed: "Save failed:",
            clearFailed: "Clear failed:",
            updateFailed: "Extra Credit update failed:",
        },
        credit: {
            none: "No extra credit",
            override: "override",
            pts: "pts",
            dash: "—",
            earlySuffix: "early",
            detailsHeading: "Extra credit details:",
            noneApplied: "No extra credit applied.",
            onTimeOrLate: "not early",
        },
        footerErrors: { attempts: "Attempts:", columns: "Columns:" },
        names: { unnamed: "(Unnamed Student)" },
    },
    en_AU: {} // fallbacks
};

const fallbackLocale = "en_US";
const getMessages = (loc) => ({ ...MESSAGES[fallbackLocale], ...(MESSAGES[loc] || {}) });
const normLocale = (loc) => String(loc || fallbackLocale).replace("_", "-");
const tpl = (s, vars = {}) =>
    String(s).replace(/\{(\w+)\}/g, (_, k) => (vars[k] == null ? "" : String(vars[k])));

/** ---------------- Endpoints ---------------- */
const ECA_PATH = "/extra-credit-associations"; // associations table
const RULES_PATH = "/extra-credit-rules";
const STORAGE_KEYS = { courseId: "courseId", activeTab: "uef-eca-active-tab" };
const makeAssociationId = (courseId, contentItemId) => `${courseId}:${contentItemId}`;

/** ---------------- Backend Adapters ---------------- */
// Rules Catalog
function makeRulesBackend(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(tenantId ? { "x-tenant-id": String(tenantId) } : {}),
        },
    });

    const listAll = async (limit = 200) => {
        if (!base) return [];
        const items = [];
        let nextKey = null;
        do {
            const res = await http.get(RULES_PATH, {
                params: { limit, ...(nextKey ? { nextKey: JSON.stringify(nextKey) } : {}) },
            });
            const page = res?.data || {};
            items.push(...(page.items || []));
            nextKey = page.nextKey || null;
        } while (nextKey);
        return items;
    };

    return { listAll };
}

// Associations (Rule + per-student Overrides + stored Credits)
function makeBackend(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(tenantId ? { "x-tenant-id": String(tenantId) } : {}),
        },
    });

    const getAllForCourse = async (courseId, limit = 200) => {
        const items = [];
        let nextKey = null;
        do {
            const res = await http.get(ECA_PATH, {
                params: { courseId, limit, ...(nextKey ? { nextKey: JSON.stringify(nextKey) } : {}) },
            });
            const page = res?.data || {};
            items.push(...(page.items || []));
            nextKey = page.nextKey || null;
        } while (nextKey);
        return items;
    };

    /** Load all associations as three maps: rules, overrides, credits */
    const loadAssociations = async (courseId) => {
        if (!base) return { rules: {}, overridesBy: {}, creditsBy: {} };
        const items = await getAllForCourse(courseId);
        const rules = {};
        const overridesBy = {}; // { userId: { contentItemId: points } }
        const creditsBy = {}; // { userId: { contentItemId: points } }
        for (const it of items) {
            const cid = it?.ContentItemId;
            if (!cid) continue;
            if (it?.Rule !== undefined) rules[cid] = it.Rule;

            const ov = it?.Overrides || {};
            for (const [userId, pts] of Object.entries(ov)) {
                if (typeof pts !== "number") continue;
                if (!overridesBy[userId]) overridesBy[userId] = {};
                overridesBy[userId][cid] = pts;
            }

            const cr = it?.Credits || {};
            for (const [userId, pts] of Object.entries(cr)) {
                if (typeof pts !== "number") continue;
                if (!creditsBy[userId]) creditsBy[userId] = {};
                creditsBy[userId][cid] = pts;
            }
        }
        return { rules, overridesBy, creditsBy, raw: items };
    };

    const buildAssociationItem = (
        courseId,
        contentItemId,
        rule,
        existingOverrides = {},
        existingCredits = {}
    ) => ({
        ID: makeAssociationId(courseId, contentItemId),
        CourseId: courseId,
        ContentItemId: contentItemId,
        Rule: rule,
        Status: "Active",
        Overrides: existingOverrides,
        Credits: existingCredits,
    });

    const upsertRule = async (
        courseId,
        contentItemId,
        rule,
        currentOverridesByUser = {},
        currentCreditsByUser = {}
    ) => {
        if (!base) return;
        const item = buildAssociationItem(
            courseId,
            contentItemId,
            rule,
            currentOverridesByUser,
            currentCreditsByUser
        );
        await http.post(ECA_PATH, [item]);
    };

    const saveRulesBulk = async (
        courseId,
        nextRulesObj = {},
        currentOverridesMap = {},
        currentCreditsMap = {}
    ) => {
        if (!base) return;
        const payload = Object.entries(nextRulesObj).map(([contentItemId, rule]) =>
            buildAssociationItem(
                courseId,
                contentItemId,
                rule,
                currentOverridesMap[contentItemId] || {},
                currentCreditsMap[contentItemId] || {}
            )
        );
        if (!payload.length) return;
        await http.post(ECA_PATH, payload);
    };

    const deleteRule = async (courseId, contentItemId) => {
        if (!base) return;
        await http.delete(ECA_PATH, { data: { CourseId: courseId, ContentItemId: contentItemId } });
    };

    const deleteAll = async (courseId) => {
        if (!base) return;
        const items = await getAllForCourse(courseId);
        await Promise.allSettled(
            (items || []).map((it) =>
                http.delete(ECA_PATH, { data: { CourseId: it.CourseId, ContentItemId: it.ContentItemId } })
            )
        );
    };

    /** Save credits by contentId -> { userId: points }, preserving overrides */
    const saveCreditsBulk = async (
        courseId,
        creditsByContent = {}, // { contentId: { userId: points } }
        currentRulesMap = {},
        currentOverridesMap = {},
        currentCreditsMap = {}
    ) => {
        if (!base) return;
        const payload = Object.entries(creditsByContent).map(([contentItemId, newMap]) => {
            const merged = { ...(currentCreditsMap[contentItemId] || {}) };
            for (const [uid, pts] of Object.entries(newMap || {})) {
                if (Number.isFinite(pts) && pts >= 0) merged[uid] = Number(pts);
            }
            return buildAssociationItem(
                courseId,
                contentItemId,
                currentRulesMap[contentItemId] || "",
                currentOverridesMap[contentItemId] || {},
                merged
            );
        });
        if (!payload.length) return;
        await http.post(ECA_PATH, payload);
    };

    const saveOverride = async (
        courseId,
        contentItemId,
        userId,
        points,
        currentRule,
        existingOverridesForContent = {},
        existingCreditsForContent = {}
    ) => {
        if (!base) return;
        const mergedOv = { ...(existingOverridesForContent || {}) };
        mergedOv[userId] = Number(points);
        const item = buildAssociationItem(
            courseId,
            contentItemId,
            currentRule || "",
            mergedOv,
            existingCreditsForContent || {}
        );
        await http.post(ECA_PATH, [item]);
    };

    const deleteOverride = async (
        courseId,
        contentItemId,
        userId,
        currentRule,
        existingOverridesForContent = {},
        existingCreditsForContent = {}
    ) => {
        if (!base) return;
        const mergedOv = { ...(existingOverridesForContent || {}) };
        delete mergedOv[userId];
        const item = buildAssociationItem(
            courseId,
            contentItemId,
            currentRule || "",
            mergedOv,
            existingCreditsForContent || {}
        );
        await http.post(ECA_PATH, [item]);
    };

    return {
        loadAssociations,
        upsertRule,
        saveRulesBulk,
        deleteRule,
        deleteAll,
        saveCreditsBulk,
        saveOverride,
        deleteOverride,
    };
}

/** ---------------- Component ---------------- */
const ExtraCreditCourseDetails = ({ platformUrl, backEndUrl, locale = "en_US", tenantId }) => {
    const M = useMemo(() => getMessages(locale), [locale]);
    const localeTag = useMemo(() => normLocale(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = queryParams.get("token");
    const courseIdFromQuery = queryParams.get("courseUuid") || "";
    const userIdParam = queryParams.get("uuid") || ""; // current user UUID

    const [courseId, setCourseId] = useState(() => {
        return courseIdFromQuery || localStorage.getItem(STORAGE_KEYS.courseId) || "";
    });
    useEffect(() => {
        if (courseIdFromQuery && courseIdFromQuery !== localStorage.getItem(STORAGE_KEYS.courseId)) {
            localStorage.setItem(STORAGE_KEYS.courseId, courseIdFromQuery);
            setCourseId(courseIdFromQuery);
        }
    }, [courseIdFromQuery]);

    // Role/membership
    const [userMembership, setUserMembership] = useState(null);
    const isInstructor = useMemo(() => {
        const r = (userMembership?.courseRoleId || "").toLowerCase();
        return r.includes("instructor");
    }, [userMembership]);

    // Tabs
    const [activeTab, setActiveTab] = useState(() => {
        return localStorage.getItem(STORAGE_KEYS.activeTab) || "set";
    });
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.activeTab, activeTab);
    }, [activeTab]);

    const [assignments, setAssignments] = useState([]);
    const [status, setStatus] = useState({ loading: true, error: null });

    // Associations state
    const [assignmentRules, setAssignmentRules] = useState({}); // { contentId: ruleCode }
    const [overridesByUser, setOverridesByUser] = useState({}); // userId -> contentId -> points
    const [creditsByUser, setCreditsByUser] = useState({}); // userId -> contentId -> points

    const overridesByContent = useMemo(() => {
        const byContent = {};
        for (const [uid, map] of Object.entries(overridesByUser)) {
            for (const [cid, pts] of Object.entries(map)) {
                if (!byContent[cid]) byContent[cid] = {};
                byContent[cid][uid] = pts;
            }
        }
        return byContent;
    }, [overridesByUser]);

    const creditsByContent = useMemo(() => {
        const byContent = {};
        for (const [uid, map] of Object.entries(creditsByUser)) {
            for (const [cid, pts] of Object.entries(map)) {
                if (!byContent[cid]) byContent[cid] = {};
                byContent[cid][uid] = pts;
            }
        }
        return byContent;
    }, [creditsByUser]);

    const [bulkRule, setBulkRule] = useState("");
    const [syncMsg, setSyncMsg] = useState("");

    // Sorting (Set tab)
    const [sortKey, setSortKey] = useState(null); // 'title' | 'rule' | null
    const [sortDir, setSortDir] = useState("asc");

    // Rules catalog
    const [ruleOptions, setRuleOptions] = useState([]); // [{ value, label, sort }]
    const [ruleLabelMap, setRuleLabelMap] = useState({}); // { Code: Label }
    const [rulesLoading, setRulesLoading] = useState(true);
    const [rulesError, setRulesError] = useState("");
    const [rulesNonce, setRulesNonce] = useState(0); // allow manual reloads

    // Gradebook columns by contentId
    const [colByContentId, setColByContentId] = useState({});
    const [columnsError, setColumnsError] = useState("");

    // Attempts indexed by userId -> contentId -> attempt
    const [attemptsBy, setAttemptsBy] = useState({});
    const [attemptsError, setAttemptsError] = useState("");

    // Students list
    const [students, setStudents] = useState([]);
    const [studentsStatus, setStudentsStatus] = useState({ loading: false, error: "" });

    // Apply tab sorting
    const [studentSortDir, setStudentSortDir] = useState("asc");
    const [assignmentColSortDir, setAssignmentColSortDir] = useState("asc");

    // Button working state
    const [updatingCredits, setUpdatingCredits] = useState(false);

    // Student view sorting
    const [studentViewSortKey, setStudentViewSortKey] = useState("title");
    const [studentViewSortDir, setStudentViewSortDir] = useState("asc");

    // Track busy save/clear in Apply grid
    const [overridesBusy, setOverridesBusy] = useState({}); // key `${userId}:${contentId}`

    // APIs
    const apiV1 = useMemo(() => {
        return axios.create({
            baseURL: `${platformUrl}/learn/api/public/v1`,
            timeout: 20000,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
    }, [platformUrl, token]);

    const apiV2 = useMemo(() => {
        return axios.create({
            baseURL: `${platformUrl}/learn/api/public/v2`,
            timeout: 20000,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
    }, [platformUrl, token]);

    const backend = useMemo(() => makeBackend(backEndUrl, tenantId), [backEndUrl, tenantId]);
    const rulesApi = useMemo(() => makeRulesBackend(backEndUrl, tenantId), [backEndUrl, tenantId]);

    /** Membership */
    useEffect(() => {
        let cancelled = false;
        async function loadMembership() {
            if (!token || !platformUrl || !courseId || !userIdParam) return;
            try {
                const res = await apiV1.get(
                    `/courses/${encodeURIComponent(courseId)}/users/uuid:${encodeURIComponent(userIdParam)}`,
                    { params: { fields: "id,userId,courseRoleId,availability" } }
                );
                if (!cancelled) setUserMembership(res?.data || null);
            } catch {
                if (!cancelled) setUserMembership(null);
            }
        }
        loadMembership();
        return () => { cancelled = true; };
    }, [apiV1, courseId, platformUrl, token, userIdParam]);

    /** Assignments */
    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!platformUrl || !token || !courseId) {
                setStatus({ loading: false, error: M.errors.missingParams });
                return;
            }
            setStatus({ loading: true, error: null });
            try {
                const allContents = await fetchAllContents(apiV1, courseId);
                const ASSIGNMENT_HANDLERS = new Set([
                    "resource/x-bb-asmt-test-link",
                    "resource/x-bb-assignment",
                ]);
                const asmt = allContents
                    .filter((c) => ASSIGNMENT_HANDLERS.has(c?.contentHandler?.id))
                    .map((c) => ({
                        id: c.id,
                        title: c.title || c.name || "(Untitled)",
                        available: c?.availability?.available || M.table.unknown,
                    }))
                    .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
                if (!cancelled) {
                    setAssignments(asmt);
                    setStatus({ loading: false, error: null });
                }
            } catch (err) {
                if (cancelled) return;
                const msg =
                    err?.response?.status === 401
                        ? M.errors.auth401
                        : err?.response?.data?.message || err?.message || M.errors.loadCourse;
                setStatus({ loading: false, error: msg });
            }
        }
        load();
        return () => { cancelled = true; };
    }, [apiV1, courseId, platformUrl, token, M]);

    /** Rules catalog */
    useEffect(() => {
        let cancelled = false;
        async function loadRulesCatalog() {
            setRulesLoading(true); setRulesError("");
            try {
                const items = await rulesApi.listAll();
                const active = (items || [])
                    .filter((r) => (r?.Status || "Active") === "Active")
                    .map((r) => ({
                        value: r?.Code ?? "",
                        label: r?.Label ?? "",
                        sort: Number.isFinite(Number(r?.SortOrder)) ? Number(r.SortOrder) : 0,
                    }))
                    .filter((r) => r.value && r.label)
                    .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
                const map = Object.fromEntries(active.map((r) => [r.value, r.label]));
                if (!cancelled) { setRuleOptions(active); setRuleLabelMap(map); }
            } catch (e) {
                if (!cancelled) {
                    setRulesError(e?.response?.data?.message || e?.message || M.errors.loadRules);
                    setRuleOptions([]); setRuleLabelMap({});
                }
            } finally { if (!cancelled) setRulesLoading(false); }
        }
        loadRulesCatalog();
        return () => { cancelled = true; };
    }, [rulesApi, M, rulesNonce]);

    /** Hydrate associations */
    useEffect(() => {
        let cancelled = false;
        async function hydrate() {
            if (!courseId) return;
            try {
                const { rules, overridesBy, creditsBy } = await backend.loadAssociations(courseId);
                if (!cancelled) {
                    setAssignmentRules(rules || {});
                    setOverridesByUser(overridesBy || {});
                    setCreditsByUser(creditsBy || {});
                }
            } catch (e) {
                setSyncMsg(
                    `Could not load rules/overrides/credits: ${e?.response?.data?.message || e.message || "error"}`
                );
                if (!cancelled) {
                    setAssignmentRules({}); setOverridesByUser({}); setCreditsByUser({});
                }
            }
        }
        hydrate();
        return () => { cancelled = true; };
    }, [backend, courseId]);

    /** Gradebook columns */
    useEffect(() => {
        let cancelled = false;
        async function loadColumns() {
            if (!courseId) return;
            setColumnsError("");
            try {
                const params = { fields: "id,contentId,score,grading,name,gradebookCategoryId" };
                const res = await apiV2.get(`/courses/${encodeURIComponent(courseId)}/gradebook/columns`, { params });
                const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
                const map = {};
                for (const c of rows) {
                    const contentId = c?.contentId;
                    if (!contentId) continue;
                    map[contentId] = {
                        id: c?.id,
                        possible: c?.score?.possible,
                        due: c?.grading?.due,
                        gradebookCategoryId: c?.gradebookCategoryId,
                    };
                }
                if (!cancelled) setColByContentId(map);
            } catch (e) {
                if (!cancelled) {
                    setColumnsError(e?.response?.data?.message || e?.message || M.errors.loadColumns);
                    setColByContentId({});
                }
            }
        }
        loadColumns();
        return () => { cancelled = true; };
    }, [apiV2, courseId, M]);

    /** Attempts */
    useEffect(() => {
        let cancelled = false;
        async function loadAllAttempts() {
            setAttemptsError("");
            try {
                const entries = Object.entries(colByContentId || {});
                if (!entries.length) return;
                const byUser = {};
                for (const [contentId, meta] of entries) {
                    const colId = meta?.id;
                    if (!colId) continue;
                    let offset = 0;
                    const limit = 100;
                    while (true) {
                        const params = { limit, offset, fields: "id,userId,attemptDate,status,created" };
                        const res = await apiV2.get(
                            `/courses/${encodeURIComponent(courseId)}/gradebook/columns/${encodeURIComponent(colId)}/attempts`,
                            { params }
                        );
                        const attempts = Array.isArray(res?.data?.results) ? res.data.results : [];
                        for (const a of attempts) {
                            const uid = a?.userId;
                            if (!uid) continue;
                            if (!byUser[uid]) byUser[uid] = {};
                            byUser[uid][contentId] = {
                                attemptDate: a?.attemptDate || a?.created || null,
                                status: a?.status || a?.attemptStatus || "",
                            };
                        }
                        if (attempts.length < limit) break;
                        offset += limit;
                    }
                }
                if (!cancelled) setAttemptsBy(byUser);
            } catch (e) {
                if (!cancelled) {
                    setAttemptsBy({});
                    setAttemptsError(e?.response?.data?.message || e?.message || M.errors.loadAttempts);
                }
            }
        }
        loadAllAttempts();
        return () => { cancelled = true; };
    }, [apiV2, courseId, colByContentId, M]);

    /** Students */
    useEffect(() => {
        let cancelled = false;
        async function loadStudents() {
            if (!courseId || !platformUrl || !token) return;
            setStudentsStatus({ loading: true, error: "" });
            try {
                const memberships = await fetchAllCourseUsers(apiV1, courseId, { expandUser: true });
                let list = [];
                if (memberships?.length) {
                    list = memberships
                        .filter((m) => String(m?.courseRoleId || "").toLowerCase().includes("student"))
                        .map((m) => {
                            const u = m?.user || {};
                            const name = u?.name || {};
                            return {
                                id: m?.id || `${m?.userId || ""}`,
                                userId: m?.userId || "",
                                given: name?.given || "",
                                family: name?.family || "",
                                displayName: u?.displayName || `${name?.given || ""} ${name?.family || ""}`.trim(),
                            };
                        });

                    const needsLookup = list.filter(
                        (s) => !s.given && !s.family && !s.displayName && s.userId
                    );
                    if (needsLookup.length) {
                        const looked = await bestEffortUserLookups(apiV1, needsLookup.map((s) => s.userId));
                        const nameMap = Object.fromEntries(
                            looked.map((u) => [
                                u.id,
                                {
                                    given: u?.name?.given || "",
                                    family: u?.name?.family || "",
                                    displayName: u?.displayName || "",
                                },
                            ])
                        );
                        list = list.map((s) => {
                            const nm = nameMap[s.userId] || {};
                            return {
                                ...s,
                                given: s.given || nm.given || "",
                                family: s.family || nm.family || "",
                                displayName: s.displayName || nm.displayName || `${nm.given || ""} ${nm.family || ""}`.trim(),
                            };
                        });
                    }
                }
                if (!cancelled) setStudents(list);
                if (!cancelled) setStudentsStatus({ loading: false, error: "" });
            } catch (e) {
                if (!cancelled) {
                    setStudents([]);
                    setStudentsStatus({ loading: false, error: e?.response?.data?.message || e?.message || M.errors.loadStudents });
                }
            }
        }
        loadStudents();
        return () => { cancelled = true; };
    }, [apiV1, courseId, platformUrl, token, M]);

    const notify = (msg) => {
        setSyncMsg(msg);
        window.clearTimeout(notify._t);
        notify._t = window.setTimeout(() => setSyncMsg(""), 4000);
    };

    const getRuleLabel = (code) => ruleLabelMap[code ?? ""] ?? "";

    /** Save/clear a rule; keep existing overrides & credits for that content item */
    const handleRuleChange = async (contentItemId, value) => {
        setAssignmentRules((prev) => ({ ...prev, [contentItemId]: value }));
        try {
            const existingOverridesForContent = overridesByContent[contentItemId] || {};
            const existingCreditsForContent = creditsByContent[contentItemId] || {};
            if (!value) {
                await backend.deleteRule(courseId, contentItemId);
                notify(M.notices.deletedOne);
                // purge overrides & credits locally
                setOverridesByUser((prev) => stripContentFromUserMaps(prev, contentItemId));
                setCreditsByUser((prev) => stripContentFromUserMaps(prev, contentItemId));
            } else {
                await backend.upsertRule(
                    courseId,
                    contentItemId,
                    value,
                    existingOverridesForContent,
                    existingCreditsForContent
                );
                notify(M.notices.savedRule);
            }
        } catch (e) {
            notify(`${M.notices.syncFailed} ${e?.response?.data?.message || e.message || "error"}`);
        }
    };

    const handleApplyAll = async () => {
        if (!bulkRule) return;
        if (assignments.length === 0) return;
        const next = {};
        for (const a of assignments) next[a.id] = bulkRule;
        setAssignmentRules(next);
        try {
            await backend.saveRulesBulk(
                courseId,
                next,
                Object.fromEntries(assignments.map((a) => [a.id, overridesByContent[a.id] || {}])),
                Object.fromEntries(assignments.map((a) => [a.id, creditsByContent[a.id] || {}]))
            );
            notify(M.notices.appliedAll);
        } catch (e) {
            notify(`${M.notices.bulkSaveFailed} ${e?.response?.data?.message || e.message || "error"}`);
        }
    };

    const handleSaveAll = async () => {
        try {
            await backend.saveRulesBulk(
                courseId,
                assignmentRules || {},
                Object.fromEntries(Object.keys(assignmentRules || {}).map((cid) => [cid, overridesByContent[cid] || {}])),
                Object.fromEntries(Object.keys(assignmentRules || {}).map((cid) => [cid, creditsByContent[cid] || {}]))
            );
            notify(M.notices.savedAll);
        } catch (e) {
            notify(`${M.notices.saveAllFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        }
    };

    const handleDeleteAll = async () => {
        setAssignmentRules({});
        setBulkRule("");
        try {
            await backend.deleteAll(courseId);
            setOverridesByUser({});
            setCreditsByUser({});
            notify(M.notices.deletedAll);
        } catch (e) {
            notify(`${M.notices.deleteAllFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        }
    };

    /** Short date for UI */
    function formatShort(iso) {
        if (!iso) return M.credit.dash;
        try {
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return M.credit.dash;
            const s = d.toLocaleString(localeTag, {
                month: "short",
                day: "2-digit",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
            return s.replace(",", "").replace(" AM", "a").replace(" PM", "p");
        } catch {
            return M.credit.dash;
        }
    }
    const friendlyDate = (iso) => formatShort(iso);

    /** How early? (vs due) */
    function earlyAmountText(dueIso, attemptIso) {
        if (!dueIso || !attemptIso) return null;
        const due = new Date(dueIso);
        const when = new Date(attemptIso);
        if (Number.isNaN(due.getTime()) || Number.isNaN(when.getTime())) return null;

        const ms = due.getTime() - when.getTime();
        if (ms <= 0) return { isEarly: false, text: M.credit.onTimeOrLate };

        let totalMinutes = Math.floor(ms / 60000);
        const days = Math.floor(totalMinutes / (60 * 24));
        totalMinutes -= days * 60 * 24;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const parts = [];
        if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
        if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
        if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);

        const text = parts.length > 0 ? `${parts.join(", ")} ${M.credit.earlySuffix}` : M.credit.onTimeOrLate;
        return { isEarly: true, text };
    }

    /** Apply an override (if present) to a computed credit object */
    function withOverrideCredit(creditObj, overridePoints) {
        if (typeof overridePoints !== "number" || overridePoints < 0) return creditObj;
        if (!creditObj) {
            return {
                applies: overridePoints > 0,
                type: "override",
                amount: overridePoints,
                computed: round2(overridePoints),
            };
        }
        return { ...creditObj, applies: overridePoints > 0, computed: round2(overridePoints), overridden: true };
    }

    /** Build feedback notes (HTML) for a student */
    function buildCreditNotesForStudent(userId) {
        const lines = [];
        for (const a of assignments) {
            const ruleCode = assignmentRules[a.id] ?? "";
            const ruleLabel = getRuleLabel(ruleCode);
            const colMeta = colByContentId[a.id] || {};
            const attempt = (attemptsBy[userId] || {})[a.id] || null;

            const base = computeExtraCredit(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
            const ovPts = overridesByUser?.[userId]?.[a.id];
            const cObj = withOverrideCredit(base, ovPts);

            if (cObj?.applies && Number.isFinite(cObj.computed) && cObj.computed > 0 && attempt?.attemptDate) {
                const whenTxt = friendlyDate(attempt.attemptDate);
                const early = earlyAmountText(colMeta?.due, attempt?.attemptDate);
                const pts = cObj.computed;
                const ovTag = typeof ovPts === "number" ? ` (${M.credit.override})` : "";
                const piece =
                    `<p><strong>${a.title}:</strong> ` +
                    `You submitted on ${whenTxt} ` +
                    (early?.isEarly ? `and were ${early.text} ` : `${M.credit.onTimeOrLate} `) +
                    `so you earned ${pts} ${M.credit.pts}${ovTag}.</p>`;
                lines.push(piece);
            }
        }
        if (!lines.length) return M.credit.noneApplied;
        return `<h6>${M.credit.detailsHeading}</h6>${lines.join("")}`;
    }

    /** Create/Find "Extra Credit" column, update scores and persist credits */
    const handleUpdateExtraCredit = async () => {
        if (!courseId || !students.length) {
            notify(M.notices.noStudentsOrCourse);
            return;
        }
        setUpdatingCredits(true);
        try {
            // carry over category id from an existing assignment column
            const candidateCategoryId =
                assignments.map((a) => colByContentId[a.id]?.gradebookCategoryId).find(Boolean);

            const colId = await getOrCreateExtraCreditColumn(apiV2, courseId, candidateCategoryId);

            // Collect credits to persist by content
            const toStoreByContent = {}; // { contentId: { userId: points } }

            let updated = 0;
            for (const s of students) {
                // total credit
                const total = computeTotalCreditForStudent(s.userId);
                const scoreToSet = total; // positive points added
                const notes = buildCreditNotesForStudent(s.userId);
                await apiV2.patch(
                    `/courses/${encodeURIComponent(courseId)}/gradebook/columns/${encodeURIComponent(colId)}/users/${encodeURIComponent(s.userId)}`,
                    { score: scoreToSet, feedback: notes }
                );
                updated++;

                // per-assignment credits snapshot
                for (const a of assignments) {
                    const colMeta = colByContentId[a.id] || {};
                    const attempt = (attemptsBy[s.userId] || {})[a.id] || null;
                    const ruleCode = assignmentRules[a.id] ?? "";
                    const ruleLabel = getRuleLabel(ruleCode);
                    const base = computeExtraCredit(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
                    const ovPts = overridesByUser?.[s.userId]?.[a.id];
                    const c = withOverrideCredit(base, ovPts);
                    const val = Number.isFinite(c?.computed) && c?.applies ? round2(c.computed) : 0;

                    if (val > 0) {
                        if (!toStoreByContent[a.id]) toStoreByContent[a.id] = {};
                        toStoreByContent[a.id][s.userId] = val;
                    }
                }
            }

            // Persist credits snapshot
            await backend.saveCreditsBulk(
                courseId,
                toStoreByContent,
                assignmentRules,
                overridesByContent,
                creditsByContent
            );

            // Update in-memory creditsByUser with what we just saved
            setCreditsByUser((prev) => {
                const next = { ...(prev || {}) };
                for (const [cid, map] of Object.entries(toStoreByContent)) {
                    for (const [uid, pts] of Object.entries(map)) {
                        if (!next[uid]) next[uid] = {};
                        next[uid] = { ...next[uid], [cid]: pts };
                    }
                }
                return next;
            });

            const studentWord = updated === 1 ? "student" : "students";
            notify(tpl(M.notices.updatedExtraCredit, { count: updated, studentWord }));
        } catch (e) {
            notify(`${M.notices.updateFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        } finally {
            setUpdatingCredits(false);
        }
    };

    /** Save/Clear override */
    const handleSaveOverride = async (contentItemId, userId, points) => {
        const busyKey = `${userId}:${contentItemId}`;
        setOverridesBusy((b) => ({ ...b, [busyKey]: true }));
        try {
            const currentRule = assignmentRules[contentItemId] || "";
            const existingForContent = overridesByContent[contentItemId] || {};
            const existingCreditsForContent = creditsByContent[contentItemId] || {};
            await backend.saveOverride(
                courseId,
                contentItemId,
                userId,
                round2(points),
                currentRule,
                existingForContent,
                existingCreditsForContent
            );
            setOverridesByUser((prev) => {
                const next = { ...(prev || {}) };
                if (!next[userId]) next[userId] = {};
                next[userId] = { ...next[userId], [contentItemId]: round2(points) };
                return next;
            });
            notify("Override saved.");
        } catch (e) {
            notify(`${M.notices.saveFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        } finally {
            setOverridesBusy((b) => {
                const n = { ...b };
                delete n[busyKey];
                return n;
            });
        }
    };

    const handleClearOverride = async (contentItemId, userId) => {
        const busyKey = `${userId}:${contentItemId}`;
        setOverridesBusy((b) => ({ ...b, [busyKey]: true }));
        try {
            const currentRule = assignmentRules[contentItemId] || "";
            const existingForContent = overridesByContent[contentItemId] || {};
            const existingCreditsForContent = creditsByContent[contentItemId] || {};
            await backend.deleteOverride(
                courseId,
                contentItemId,
                userId,
                currentRule,
                existingForContent,
                existingCreditsForContent
            );
            setOverridesByUser((prev) => {
                const copy = { ...(prev || {}) };
                const map = { ...(copy[userId] || {}) };
                delete map[contentItemId];
                copy[userId] = map;
                return copy;
            });
            notify("Override cleared.");
        } catch (e) {
            notify(`${M.notices.clearFailed} ${e?.response?.data?.message || e?.message || "error"}`);
        } finally {
            setOverridesBusy((b) => {
                const n = { ...b };
                delete n[busyKey];
                return n;
            });
        }
    };

    /** Sorting helpers (Set tab) */
    const toggleSort = (key) => {
        setSortKey((prevKey) => {
            if (prevKey === key) {
                setSortDir((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
                return prevKey;
            } else {
                setSortDir("asc");
                return key;
            }
        });
    };
    const sortedAssignments = useMemo(() => {
        const list = assignments.slice();
        if (!sortKey) return list;
        const dir = sortDir === "asc" ? 1 : -1;

        if (sortKey === "title") {
            list.sort((a, b) => {
                const tA = (a.title || "").toLocaleLowerCase();
                const tB = (b.title || "").toLocaleLowerCase();
                if (tA < tB) return -1 * dir;
                if (tA > tB) return 1 * dir;
                const rA = getRuleLabel(assignmentRules[a.id] ?? "");
                const rB = getRuleLabel(assignmentRules[b.id] ?? "");
                return rA.localeCompare(rB) * dir;
            });
        } else if (sortKey === "rule") {
            list.sort((a, b) => {
                const rA = getRuleLabel(assignmentRules[a.id] ?? "");
                const rB = getRuleLabel(assignmentRules[b.id] ?? "");
                const cmp = rA.localeCompare(rB);
                if (cmp !== 0) return cmp * dir;
                const tA = (a.title || "").toLocaleLowerCase();
                const tB = (b.title || "").toLocaleLowerCase();
                if (tA < tB) return -1 * dir;
                if (tA > tB) return 1 * dir;
                return 0;
            });
        }
        return list;
    }, [assignments, assignmentRules, sortKey, sortDir, ruleLabelMap]);

    // Apply tab: sorted students and sorted assignment columns
    const sortedStudents = useMemo(() => {
        const list = students.slice();
        const dir = studentSortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            const aLast = (a.family || "").toLocaleLowerCase();
            const bLast = (b.family || "").toLocaleLowerCase();
            if (aLast < bLast) return -1 * dir;
            if (aLast > bLast) return 1 * dir;
            const aFirst = (a.given || "").toLocaleLowerCase();
            const bFirst = (b.given || "").toLocaleLowerCase();
            if (aFirst < bFirst) return -1 * dir;
            if (aFirst > bFirst) return 1 * dir;
            return 0;
        });
        return list;
    }, [students, studentSortDir]);

    const sortedAssignmentsForApply = useMemo(() => {
        const list = assignments.slice();
        const dir = assignmentColSortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            const tA = (a.title || "").toLocaleLowerCase();
            const tB = (b.title || "").toLocaleLowerCase();
            if (tA < tB) return -1 * dir;
            if (tA > tB) return 1 * dir;
            return 0;
        });
        return list;
    }, [assignments, assignmentColSortDir]);

    /** Prefer UUID if keyed, else PK1 from membership */
    const currentUserAttemptKey = useMemo(() => {
        if (userIdParam && attemptsBy[userIdParam]) return userIdParam;
        const pk1 = userMembership?.userId || "";
        if (pk1 && attemptsBy[pk1]) return pk1;
        return userIdParam || (userMembership?.userId || "");
    }, [attemptsBy, userIdParam, userMembership]);

    /** Student view rows */
    const studentRows = useMemo(() => {
        if (!currentUserAttemptKey) return [];
        return assignments.map((a) => {
            const meta = colByContentId[a.id] || {};
            const attempt = (attemptsBy[currentUserAttemptKey] || {})[a.id] || null;
            const ruleCode = assignmentRules[a.id] ?? "";
            const ruleLabel = getRuleLabel(ruleCode);
            const base = computeExtraCredit(meta?.possible, meta?.due, attempt?.attemptDate, ruleLabel);
            const ovPts = overridesByUser?.[currentUserAttemptKey]?.[a.id];
            const creditObj = withOverrideCredit(base, ovPts);
            return {
                id: a.id,
                title: a.title,
                rule: ruleLabel || "",
                due: meta?.due || null,
                possible: Number.isFinite(meta?.possible) ? meta.possible : null,
                attempt: attempt?.attemptDate || null,
                credit: creditObj,
            };
        });
    }, [assignments, colByContentId, attemptsBy, assignmentRules, currentUserAttemptKey, ruleLabelMap, overridesByUser]);

    /** Sorted student rows */
    const sortedStudentRows = useMemo(() => {
        const rows = studentRows.slice();
        const dir = studentViewSortDir === "asc" ? 1 : -1;
        rows.sort((a, b) => {
            const key = studentViewSortKey;
            const va = a[key];
            const vb = b[key];
            if (key === "title" || key === "rule") {
                const ta = (va || "").toLocaleLowerCase();
                const tb = (vb || "").toLocaleLowerCase();
                if (ta < tb) return -1 * dir;
                if (ta > tb) return 1 * dir;
                return 0;
            }
            if (key === "due" || key === "attempt") {
                const da = va ? new Date(va).getTime() : 0;
                const db = vb ? new Date(vb).getTime() : 0;
                return (da - db) * dir;
            }
            if (key === "possible") {
                const pa = Number.isFinite(va) ? va : -Infinity;
                const pb = Number.isFinite(vb) ? vb : -Infinity;
                if (pa < pb) return -1 * dir;
                if (pa > pb) return 1 * dir;
                return 0;
            }
            if (key === "credit") {
                const pa = a.credit?.computed ?? -Infinity;
                const pb = b.credit?.computed ?? -Infinity;
                if (pa < pb) return -1 * dir;
                if (pa > pb) return 1 * dir;
                return 0;
            }
            return 0;
        });
        return rows;
    }, [studentRows, studentViewSortKey, studentViewSortDir]);

    const ariaSort = (key) => (sortKey === key ? (sortDir === "asc" ? "ascending" : "descending") : "none");
    const sortGlyph = (key) => (sortKey !== key ? "↕" : sortDir === "asc" ? "▲" : "▼");
    const colSortGlyph = assignmentColSortDir === "asc" ? "▲" : "▼";
    const studentSortGlyph = studentViewSortDir === "asc" ? "▲" : "▼";

    /** Render gates */
    if (status.loading) {
        return <div className="bb-container" style={{ padding: 16 }}>{M.loading.assignments}</div>;
    }
    if (status.error) {
        return <div className="bb-container" style={{ padding: 16, color: "#b00020" }}>{status.error}</div>;
    }

    /** If not instructor, render the Student view */
    if (!isInstructor) {
        const totalCredit = computeTotalCreditForStudent(currentUserAttemptKey);
        return (
            <div className="bb-container" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
                <section className="bb-card" style={cardStyle}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <h2 style={h2Style}>{M.headings.student}</h2>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>
                            {M.headings.total} <span title={M.tooltips.total}>+{totalCredit} {M.credit.pts}</span>
                        </div>
                    </div>

                    <div style={{ width: "100%", overflow: "auto" }}>
                        <table className="bb-table" style={listTableStyle}>
                            <thead>
                            <tr>
                                {studentHeaderCell(M.table.assignment, "title", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir })}
                                {studentHeaderCell(M.table.rule, "rule", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir })}
                                {studentHeaderCell(M.table.due, "due", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir })}
                                {studentHeaderCell(M.table.points, "possible", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir })}
                                {studentHeaderCell(M.table.attempted, "attempt", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir })}
                                {studentHeaderCell(M.table.credit, "credit", { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir })}
                            </tr>
                            </thead>
                            <tbody>
                            {sortedStudentRows.length === 0 ? (
                                <tr><td colSpan={6} style={{ textAlign: "center" }}>{M.table.noAssignments}</td></tr>
                            ) : (
                                sortedStudentRows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.title}</td>
                                        <td>{r.rule || M.credit.dash}</td>
                                        <td>{formatDue(r.due, localeTag)}</td>
                                        <td>{Number.isFinite(r.possible) ? r.possible : M.credit.dash}</td>
                                        <td>{formatDue(r.attempt, localeTag)}</td>
                                        <td>{formatCredit(r.credit, M)}</td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {(attemptsError || columnsError) && (
                        <div style={{ marginTop: 12 }}>
                            {attemptsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.attempts} {attemptsError}</div>}
                            {columnsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.columns} {columnsError}</div>}
                        </div>
                    )}
                </section>
            </div>
        );
    }

    /** Instructor view */
    return (
        <div className="bb-container" style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
            {/* Tabs */}
            <div role="tablist" aria-label="Extra Credit Tabs" style={tabsRowStyle}>
                <button
                    role="tab"
                    aria-selected={activeTab === "set"}
                    aria-controls="panel-set"
                    id="tab-set"
                    onClick={() => setActiveTab("set")}
                    style={{ ...tabBtn, ...(activeTab === "set" ? tabBtnActive : {}) }}
                >
                    {M.tabs.set}
                </button>
                <button
                    role="tab"
                    aria-selected={activeTab === "apply"}
                    aria-controls="panel-apply"
                    id="tab-apply"
                    onClick={() => setActiveTab("apply")}
                    style={{ ...tabBtn, ...(activeTab === "apply" ? tabBtnActive : {}) }}
                >
                    {M.tabs.apply}
                </button>
            </div>

            {/* Panels */}
            {activeTab === "set" ? (
                <section id="panel-set" role="tabpanel" aria-labelledby="tab-set" className="bb-card" style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                        <h2 style={h2Style}>{M.headings.assoc}</h2>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" onClick={handleSaveAll} style={primaryBtn}>{M.buttons.saveAll}</button>
                            <button type="button" onClick={handleDeleteAll} style={dangerBtn}>{M.buttons.deleteAll}</button>
                        </div>
                    </div>

                    {/* Bulk apply row */}
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <label>{M.buttons.applyToAll}</label>

                            {rulesLoading ? (
                                <span style={{ fontSize: 12, color: "#666" }}>{M.loading.rules}</span>
                            ) : rulesError ? (
                                <>
                                    <span style={{ fontSize: 12, color: "#b91c1c" }}>{rulesError}</span>
                                    <button
                                        type="button"
                                        onClick={() => setRulesNonce((n) => n + 1)}
                                        style={linkBtn}
                                    >
                                        {M.buttons.retry}
                                    </button>
                                </>
                            ) : (
                                <select value={bulkRule} onChange={(e) => setBulkRule(e.target.value)} style={selectStyle}>
                                    <option value="">{M.buttons.clearOption}</option>
                                    {ruleOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}

                            <button type="button" onClick={handleApplyAll} style={primaryBtn} disabled={!bulkRule || rulesLoading}>
                                {M.buttons.applyAndSave}
                            </button>
                        </div>
                    </div>

                    {syncMsg && <div style={toastStyle} role="status" aria-live="polite">{syncMsg}</div>}

                    <table className="bb-table" style={listTableStyle}>
                        <thead>
                        <tr>
                            <th style={{ width: "10%" }}>{M.table.actions}</th>

                            <th
                                style={{ width: "45%", cursor: "pointer", userSelect: "none" }}
                                role="button"
                                tabIndex={0}
                                aria-sort={ariaSort("title")}
                                onClick={() => toggleSort("title")}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("title")}
                                title={tpl(M.tooltips.sortBy, { label: M.table.assignment })}
                            >
                                {M.table.assignment} <span style={sortIconStyle}>{sortGlyph("title")}</span>
                            </th>

                            <th
                                style={{ width: "45%", cursor: "pointer", userSelect: "none" }}
                                role="button"
                                tabIndex={0}
                                aria-sort={ariaSort("rule")}
                                onClick={() => toggleSort("rule")}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("rule")}
                                title={tpl(M.tooltips.sortBy, { label: M.table.rule })}
                            >
                                {M.table.rule} <span style={sortIconStyle}>{sortGlyph("rule")}</span>
                            </th>
                        </tr>
                        </thead>

                        <tbody>
                        {sortedAssignments.length === 0 ? (
                            <tr><td colSpan={3} style={{ textAlign: "center" }}>{M.table.noAssignments}</td></tr>
                        ) : sortedAssignments.map((a) => {
                            const selected = assignmentRules[a.id] ?? "";
                            const col = colByContentId[a.id];
                            const possible = Number.isFinite(col?.possible) ? col.possible : null;
                            const dueIso = col?.due || null;
                            return (
                                <tr key={a.id}>
                                    <td style={{ textAlign: "center" }}>
                                        <button
                                            type="button"
                                            onClick={() => handleRuleChange(a.id, "")}
                                            style={iconBtn}
                                            title={tpl(M.tooltips.clearRuleFor, { title: a.title })}
                                            aria-label={tpl(M.tooltips.clearRuleFor, { title: a.title })}
                                        >
                                            ❌
                                        </button>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{a.title}</div>
                                        <div style={{ fontSize: 12, color: "#666" }}>
                                            {a.available || M.table.unknown}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#444", marginTop: 4 }}>
                                            <span><strong>{M.meta.points}</strong> {possible ?? M.credit.dash}</span>
                                            <span style={{ margin: "0 8px" }}>·</span>
                                            <span><strong>{M.meta.due}</strong> {formatDue(dueIso, localeTag)}</span>
                                            {columnsError && (
                                                <>
                                                    <span style={{ margin: "0 8px" }}>·</span>
                                                    <span style={{ color: "#b91c1c" }} title={columnsError}>{M.table.columnsUnavailable}</span>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {rulesLoading ? (
                                            <span style={{ fontSize: 12, color: "#666" }}>{M.loading.generic}</span>
                                        ) : rulesError ? (
                                            <span style={{ fontSize: 12, color: "#b91c1c" }}>{M.table.rulesUnavailable}</span>
                                        ) : (
                                            <select
                                                value={selected}
                                                onChange={(e) => handleRuleChange(a.id, e.target.value)}
                                                style={selectStyle}
                                                aria-label={tpl(M.tooltips.selectRuleFor, { title: a.title })}
                                            >
                                                <option value="">{M.buttons.clearOption}</option>
                                                {ruleOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </section>
            ) : (
                // --- APPLY RULES TAB ---
                <section id="panel-apply" role="tabpanel" aria-labelledby="tab-apply" className="bb-card" style={{ ...cardStyle, padding: 0 }}>
                    <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <h2 style={h2Style}>{M.headings.apply}</h2>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                onClick={() => setAssignmentColSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                style={secondaryBtn}
                                title={M.tooltips.sortByAssignment}
                            >
                                {assignmentColSortDir === "asc" ? M.buttons.sortAssignmentsAZ : M.buttons.sortAssignmentsZA} {colSortGlyph}
                            </button>
                            <button
                                type="button"
                                onClick={() => setStudentSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                style={secondaryBtn}
                                title={M.tooltips.sortByStudent}
                            >
                                {studentSortDir === "asc" ? M.buttons.sortStudentsAZ : M.buttons.sortStudentsZA} {colSortGlyph}
                            </button>
                            <button
                                type="button"
                                onClick={handleUpdateExtraCredit}
                                style={{ ...primaryBtn, opacity: updatingCredits ? 0.7 : 1 }}
                                disabled={updatingCredits || studentsStatus.loading || !students.length}
                                title={M.tooltips.updateExtraCredit}
                            >
                                {updatingCredits ? M.buttons.updating : M.buttons.updateExtraCredit}
                            </button>
                        </div>
                    </div>

                    {(studentsStatus.loading || !students.length) && (
                        <div style={{ padding: 12 }}>
                            {studentsStatus.loading ? M.loading.students : M.table.noStudents}
                            {studentsStatus.error && (
                                <div style={{ marginTop: 6, color: "#b91c1c", fontSize: 12 }}>{studentsStatus.error}</div>
                            )}
                        </div>
                    )}

                    {!!students.length && (
                        <div style={{ width: "100%", overflow: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                                <thead>
                                <tr>
                                    <th
                                        style={{ position: "sticky", left: 0, zIndex: 3, background: "#f9fafb", boxShadow: "2px 0 0 #e5e7eb", minWidth: 240, padding: "8px 10px", borderBottom: "1px solid #e5e7eb", cursor: "pointer", textAlign: "left", whiteSpace: "nowrap" }}
                                        onClick={() => setStudentSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                        title={M.tooltips.sortByStudent}
                                    >
                                        {M.table.student} <span style={sortIconStyle}>{studentSortGlyph}</span>
                                    </th>
                                    {sortedAssignmentsForApply.map((a) => {
                                        const ruleCode = assignmentRules[a.id] ?? "";
                                        const ruleLabel = getRuleLabel(ruleCode);
                                        return (
                                            <th
                                                key={a.id}
                                                style={{ position: "sticky", top: 0, zIndex: 2, background: "#f9fafb", borderBottom: "1px solid #e5e7eb", minWidth: 260, padding: "8px 10px", textAlign: "left", whiteSpace: "nowrap", cursor: "pointer" }}
                                                onClick={() => setAssignmentColSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                                title={M.tooltips.sortByAssignment}
                                            >
                                                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {a.title}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#374151", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {ruleLabel || M.credit.dash}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {formatDue(colByContentId[a.id]?.due, localeTag) || ""}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                                </thead>
                                <tbody>
                                {sortedStudents.map((s) => {
                                    const name = formatStudentName(s, M);
                                    return (
                                        <tr key={s.userId || s.id}>
                                            <td style={{ position: "sticky", left: 0, zIndex: 3, background: "#fff", boxShadow: "2px 0 0 #e5e7eb", padding: "8px 10px", borderBottom: "1px solid #f3f4f6", whiteSpace: "nowrap" }}>
                                                <div style={{ fontWeight: 600 }}>{name}</div>
                                            </td>
                                            {sortedAssignmentsForApply.map((a) => {
                                                const colMeta = colByContentId[a.id] || {};
                                                const attempt = (attemptsBy[s.userId] || {})[a.id] || null;
                                                const ruleCode = assignmentRules[a.id] ?? "";
                                                const ruleLabel = getRuleLabel(ruleCode);

                                                const base = computeExtraCredit(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
                                                const ovPts = overridesByUser?.[s.userId]?.[a.id];
                                                const credit = withOverrideCredit(base, ovPts);

                                                const busyKey = `${s.userId}:${a.id}`;
                                                const isBusy = !!overridesBusy[busyKey];
                                                const inputId = `ov-${s.userId}-${a.id}`;

                                                return (
                                                    <td key={a.id} style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle", whiteSpace: "nowrap" }} title={`${name} × ${a.title}`}>
                                                        {attempt ? (
                                                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                                                                <div><strong>{M.attemptCard.attempt}</strong> {formatDue(attempt.attemptDate, localeTag)}</div>
                                                                <div><strong>{M.attemptCard.status}</strong> {attempt.status || M.credit.dash}</div>
                                                                <div>
                                                                    <strong>{M.attemptCard.credit}</strong> {formatCredit(credit, M)}
                                                                    {typeof ovPts === "number" && <span style={overrideChip}>{M.chips.overridden}</span>}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: 12, lineHeight: 1.35, color: "#6b7280" }}>
                                                                <div><em>{M.attemptCard.noAttempt}</em></div>
                                                                <div><strong>{M.meta.due}</strong> {formatDue(colMeta?.due, localeTag)}</div>
                                                                <div><strong>{M.attemptCard.credit}</strong> {M.credit.dash}</div>
                                                            </div>
                                                        )}

                                                        {/* Override controls */}
                                                        <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                                            <label htmlFor={inputId} style={{ fontSize: 11 }}>{M.attemptCard.overrideLabel}</label>
                                                            <input
                                                                id={inputId}
                                                                type="number"
                                                                min={0}
                                                                step="0.01"
                                                                defaultValue={typeof ovPts === "number" ? ovPts : (credit?.computed ?? 0)}
                                                                style={{ width: 90, padding: "4px 6px", border: "1px solid #e5e7eb", borderRadius: 6 }}
                                                                aria-label={`${M.attemptCard.overrideLabel} ${name} / ${a.title}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                disabled={isBusy}
                                                                onClick={() => {
                                                                    const el = document.getElementById(inputId);
                                                                    const raw = el ? el.value : "";
                                                                    const val = Number(raw);
                                                                    if (!Number.isFinite(val) || val < 0) {
                                                                        notify(M.errors.enterNonNegative);
                                                                        return;
                                                                    }
                                                                    handleSaveOverride(a.id, s.userId, val);
                                                                }}
                                                                style={{ padding: "4px 8px", borderRadius: 6, background: "#111827", color: "#fff", fontSize: 12 }}
                                                            >
                                                                {isBusy ? M.buttons.saving : M.buttons.save}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={isBusy || typeof ovPts !== "number"}
                                                                onClick={() => handleClearOverride(a.id, s.userId)}
                                                                style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#111827", fontSize: 12 }}
                                                            >
                                                                {M.buttons.clear}
                                                            </button>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {(attemptsError || columnsError) && (
                        <div style={{ padding: 12 }}>
                            {attemptsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.attempts} {attemptsError}</div>}
                            {columnsError && <div style={{ color: "#b91c1c", fontSize: 12 }}>{M.footerErrors.columns} {columnsError}</div>}
                        </div>
                    )}

                    {syncMsg && <div style={{ padding: 12 }}><div style={toastStyle} role="status" aria-live="polite">{syncMsg}</div></div>}
                </section>
            )}
        </div>
    );

    /** ---- local helpers that need component scope ---- */
    function computeTotalCreditForStudent(userId) {
        let total = 0;
        for (const a of assignments) {
            const ruleCode = assignmentRules[a.id] ?? "";
            const ruleLabel = getRuleLabel(ruleCode);
            const colMeta = colByContentId[a.id] || {};
            const attempt = (attemptsBy[userId] || {})[a.id] || null;
            const base = computeExtraCredit(colMeta?.possible, colMeta?.due, attempt?.attemptDate, ruleLabel);
            const ovPts = overridesByUser?.[userId]?.[a.id];
            const c = (typeof ovPts === "number")
                ? { applies: ovPts > 0, computed: round2(ovPts) }
                : base;
            if (c?.applies && Number.isFinite(c.computed)) total += Number(c.computed) || 0;
        }
        return round2(total);
    }
};

/** ---------------- Helpers (pure) ---------------- */
function studentHeaderCell(label, key, { studentViewSortKey, studentViewSortDir, setStudentViewSortKey, setStudentViewSortDir }) {
    const active = studentViewSortKey === key;
    const glyph = !active ? "↕" : studentViewSortDir === "asc" ? "▲" : "▼";
    return (
        <th
            style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
            onClick={() => {
                if (active) {
                    setStudentViewSortDir((d) => (d === "asc" ? "desc" : "asc"));
                } else {
                    setStudentViewSortKey(key);
                    setStudentViewSortDir("asc");
                }
            }}
            title={`Sort by ${label}`}
        >
            {label} <span style={{ fontSize: 12, opacity: 0.6, marginLeft: 6 }}>{glyph}</span>
        </th>
    );
}

function formatDue(iso, localeTag = "en-US") {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleString(localeTag, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch {
        return "—";
    }
}

function formatStudentName(s, M) {
    const given = (s?.given || "").trim();
    const family = (s?.family || "").trim();
    const dn = (s?.displayName || "").trim();
    if (given || family) return `${given} ${family}`.trim();
    if (dn) return dn;
    return M.names.unnamed;
}

async function fetchAllContents(api, courseId) {
    const results = [];
    const pageThroughLocal = async (path, params = {}) => {
        let offset = 0;
        const limit = 100;
        while (true) {
            const res = await api.get(path, { params: { ...params, limit, offset } });
            const rows = Array.isArray(res.data?.results) ? res.data.results : [];
            results.push(...rows);
            if (rows.length < limit) break;
            offset += limit;
        }
    };
    try {
        await pageThroughLocal(`/courses/${encodeURIComponent(courseId)}/contents`);
    } catch {
        return results;
    }
    return results;
}

/** Fetch all course memberships */
async function fetchAllCourseUsers(apiV1, courseId, { expandUser = true } = {}) {
    const collectPaged = async (path) => {
        const out = [];
        let offset = 0;
        const limit = 100;
        while (true) {
            const params = {
                limit,
                offset,
                ...(expandUser ? { expand: "user" } : {}),
                fields: "id,userId,courseRoleId,availability,user",
            };
            const res = await apiV1.get(path, { params });
            const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
            out.push(...rows);
            if (rows.length < limit) break;
            offset += limit;
        }
        return out;
    };

    try {
        const rows = await collectPaged(`/courses/${encodeURIComponent(courseId)}/users`);
        if (rows.length) return rows;
    } catch { /* fall through */ }

    const rows2 = await collectPaged(`/courses/${encodeURIComponent(courseId)}/memberships`);
    return rows2;
}

/** Best-effort batched user lookups */
async function bestEffortUserLookups(apiV1, userIds = []) {
    const uniq = Array.from(new Set(userIds.filter(Boolean)));
    const out = [];
    const pool = 5;
    let i = 0;
    async function next() {
        if (i >= uniq.length) return;
        const id = uniq[i++];
        try {
            const res = await apiV1.get(`/users/${encodeURIComponent(id)}`, { params: { fields: "id,name,displayName" } });
            if (res?.data) out.push(res.data);
        } catch { /* ignore */ }
        return next();
    }
    const workers = Array.from({ length: Math.min(pool, uniq.length) }, () => next());
    await Promise.all(workers);
    return out;
}

/** find or create the "Extra Credit" column with points possible = 0 */
async function getOrCreateExtraCreditColumn(apiV2, courseId, gradebookCategoryId) {
    const params = { fields: "id,name,score,gradebookCategoryId" };
    const res = await apiV2.get(`/courses/${encodeURIComponent(courseId)}/gradebook/columns`, { params });
    const rows = Array.isArray(res?.data?.results) ? res.data.results : [];
    const existing = rows.find(
        (c) => (c?.name || "").trim().toLowerCase() === "extra credit"
    );
    if (existing?.id) return existing.id;

    const createBody = {
        name: "Extra Credit",
        score: { possible: 0 },
        availability: { available: "Yes" },
        ...(gradebookCategoryId ? { gradebookCategoryId } : {}),
    };
    const createRes = await apiV2.post(
        `/courses/${encodeURIComponent(courseId)}/gradebook/columns`,
        createBody
    );
    return createRes?.data?.id;
}

/** ---------------- Extra Credit parsing & computation ---------------- */
/*
Supported rule label formats (mirror your Admin component):
1)  "<X>% extra if more than <N> <hours|days> early"
2)  "<X> points extra if more than <N> <hours|days> early"
3)  "<X>% extra each day early" | "<X>% extra for every <N> <hours|days> early" (optional cap: "(up to 30%)" or "max 30%")
4)  "<X> points extra for every <N> <hours|days> early" (optional cap: "up to 10 points" / "max 10 points")
*/
function parseEarlyRuleLabel(label = "") {
    const txt = String(label).toLowerCase().trim();

    // 1) percent if more than threshold early
    let m = txt.match(/(\d+(?:\.\d+)?)\s*%\s*(?:points?)?\s*extra\s*if\s*more\s*than\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*early/);
    if (m) {
        return { type: "percent_threshold_early", amount: parseFloat(m[1]), threshold: parseFloat(m[2]), unit: m[3].startsWith("hour") ? "hours" : "days" };
    }

    // 2) points if more than threshold early
    m = txt.match(/(\d+(?:\.\d+)?)\s*points?\s*extra\s*if\s*more\s*than\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*early/);
    if (m) {
        return { type: "points_threshold_early", points: parseFloat(m[1]), threshold: parseFloat(m[2]), unit: m[3].startsWith("hour") ? "hours" : "days" };
    }

    // 3) percent per interval, optional explicit interval + optional cap
    m = txt.match(/(\d+(?:\.\d+)?)\s*%\s*(?:points?)?\s*extra\s*(?:each|for\s*every)\s*(?:(\d+(?:\.\d+)?)\s*)?(hour|hours|day|days)\s*early(?:\s*\((?:up\s*to\s*(\d+(?:\.\d+)?)\s*%|max\s*(\d+(?:\.\d+)?)\s*%)\))?/);
    if (m) {
        return {
            type: "percent_per_interval_early",
            amount: parseFloat(m[1]),
            interval: m[2] !== undefined ? parseFloat(m[2]) : 1,
            unit: m[3].startsWith("hour") ? "hours" : "days",
            cap: m[4] ? parseFloat(m[4]) : (m[5] ? parseFloat(m[5]) : null),
        };
    }

    // 4) points per interval, optional cap
    m = txt.match(/(\d+(?:\.\d+)?)\s*points?\s*extra\s*(?:for\s*every|each)\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*early(?:\s*(?:up\s*to|for\s*a\s*max(?:imum)?)\s*(\d+(?:\.\d+)?)\s*points?)?/);
    if (m) {
        return { type: "points_per_interval_early", rate: parseFloat(m[1]), interval: parseFloat(m[2]), unit: m[3].startsWith("hour") ? "hours" : "days", cap: m[4] !== undefined ? parseFloat(m[4]) : null };
    }

    return null;
}

function computeExtraCredit(possible, dueIso, attemptIso, ruleLabel) {
    if (!ruleLabel || !possible || !dueIso || !attemptIso) return null;

    const spec = parseEarlyRuleLabel(ruleLabel);
    if (!spec) return null;

    const due = new Date(dueIso);
    const when = new Date(attemptIso);
    if (Number.isNaN(due.getTime()) || Number.isNaN(when.getTime())) return null;

    const earlyMs = due.getTime() - when.getTime(); // positive means early
    if (earlyMs <= 0) return { applies: false, type: spec.type, computed: 0 };

    // helpers
    const HOUR = 3600_000, DAY = 86400_000;
    const unitMs = spec.unit === "hours" ? HOUR : DAY;

    if (spec.type === "percent_threshold_early") {
        const thresholdMs = (spec.threshold || 0) * unitMs;
        if (earlyMs <= thresholdMs) return { applies: false, type: spec.type, computed: 0 };
        const computed = round2((possible * (spec.amount || 0)) / 100);
        return { applies: computed > 0, type: "percent", amount: spec.amount, computed };
    }

    if (spec.type === "points_threshold_early") {
        const thresholdMs = (spec.threshold || 0) * unitMs;
        if (earlyMs <= thresholdMs) return { applies: false, type: spec.type, computed: 0 };
        const computed = round2(spec.points || 0);
        return { applies: computed > 0, type: "points", amount: spec.points, computed };
    }

    if (spec.type === "percent_per_interval_early") {
        const intervals = Math.floor(earlyMs / (unitMs * (spec.interval || 1)));
        let totalPct = round2((spec.amount || 0) * intervals);
        if (Number.isFinite(spec.cap) && spec.cap != null) totalPct = Math.min(totalPct, round2(spec.cap));
        const computed = round2((possible * totalPct) / 100);
        return { applies: computed > 0, type: "percent_per_interval", amount: totalPct, computed };
    }

    if (spec.type === "points_per_interval_early") {
        const intervals = Math.floor(earlyMs / (unitMs * (spec.interval || 1)));
        let computed = round2((spec.rate || 0) * intervals);
        if (spec.cap != null && Number.isFinite(spec.cap)) computed = Math.min(computed, round2(spec.cap));
        return { applies: computed > 0, type: "points_per_interval", rate: spec.rate, computed };
    }

    return null;
}

function formatCredit(c, M) {
    if (!c) return M.credit.dash;
    if (!c.applies) return M.credit.none;
    if (c.type === "percent" || c.type === "percent_per_interval") return `+${c.computed} ${M.credit.pts}`;
    if (c.type === "points" || c.type === "points_per_interval") return `+${c.computed} ${M.credit.pts}`;
    if (c.type === "override") return `+${c.computed} ${M.credit.pts} (${M.credit.override})`;
    if (c.overridden) return `+${c.computed} ${M.credit.pts} (${M.credit.override})`;
    return M.credit.dash;
}

function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

function stripContentFromUserMaps(prev, contentItemId) {
    const copy = {};
    for (const [uid, map] of Object.entries(prev || {})) {
        const m = { ...(map || {}) };
        delete m[contentItemId];
        copy[uid] = m;
    }
    return copy;
}

/** ---------------- Styles ---------------- */
const tabsRowStyle = {
    display: "flex",
    gap: 8,
    marginBottom: 12,
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 4,
};
const tabBtn = {
    padding: "8px 12px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    borderBottom: "none",
    background: "#fff",
    color: "#111827",
};
const tabBtnActive = {
    background: "#111827",
    color: "#fff",
    borderColor: "#111827",
};

const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 16 };
const h2Style = { fontSize: 18, fontWeight: "bold", margin: "0 0 12px" };
const listTableStyle = { width: "100%", borderCollapse: "collapse" };
const selectStyle = { width: "100%", padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" };
const primaryBtn = { padding: "6px 12px", borderRadius: 8, background: "#111827", color: "#fff" };
const secondaryBtn = { padding: "6px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#111827" };
const dangerBtn = { padding: "6px 12px", border: "1px solid #dc2626", borderRadius: 8, background: "#fff", color: "#dc2626" };
const toastStyle = { margin: "8px 0", padding: "8px", background: "#ecfdf5", color: "#065f46", borderRadius: 6 };
const iconBtn = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "10px",
    lineHeight: "1",
    color: "#b91c1c",
    padding: 0,
    margin: 0
};
const linkBtn = {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#2563eb",
    cursor: "pointer",
    textDecoration: "underline"
};
const sortIconStyle = { fontSize: 12, opacity: 0.6, marginLeft: 6 };
const overrideChip = {
    marginLeft: 8,
    padding: "2px 6px",
    fontSize: 10,
    borderRadius: 999,
    background: "#ecfeff",
    color: "#155e75",
    border: "1px solid #67e8f9",
};

export default ExtraCreditCourseDetails;
